import { Box, Flex } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Comic, LayoutStyle, ServerListResponse } from "../../types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIsSmallBreakpoint, useMobileBreakpoint } from "../../utils/useBreakpoints";
import { useInvalidateQueries } from "../../services/axiosInstance";
import { libraryFilterStore } from "../../hookstate/libraryFiltersStore";
import { SortMenus } from "../../components/sortMenus/SortMenus";
import { GET_LIBRARY_LIST_QUERY_KEY } from "./api/libraryScreenApi";
import { useHookstate } from "@hookstate/core";
import { useLibraryAPI } from "./hooks/useLibraryAPI";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { comicsResultsCardV2 } from "../../components/resultsGrid/ComicsResultsCard";
import { FluidVirtualGrid } from "../../components/resultsGrid/FluidVirtualGrid";
import { sizeEstimator } from "../../components/cards/hooks/useGridCardDimensions";
import { ReadingStatusCarousel } from "./components/LibraryFilterCarousel";
import { DEFAULT_READING_STATUS_TYPE } from "../../hookstate/libraryFiltersStore";
import { throttle } from "lodash-es";
import { useOutletContext } from "react-router";
import { useTitleOnNavigation } from "../../hooks/useTitleOnNavigation";
import { appStore } from "../../hookstate/appStore";
import { useTopBarHeight } from "../../hooks/userTopBarHeight";
import { useSubHeaderScroll } from "../../hooks/useSubHeaderScroll";
import { useVirtualScrollRestoration } from "../../hooks/useVirtualScrollRestoration";
import { ShelfOutletContext } from "../shelf/MyShelfScreen";

export default function LibraryScreen() {
  const [t] = useTranslation();
  const isSmall = useIsSmallBreakpoint();
  const isMobile = useMobileBreakpoint();
  const topBarHeight = useTopBarHeight();
  const { stickyTop: subHeaderTop, stickyTransform, stickyTransition } =
    useSubHeaderScroll();

  // Scroll restoration - reset scroll when filters change
  const readingStatus = useHookstate(libraryFilterStore.comicReadingStatus).get();
  const sortOrder = useHookstate(libraryFilterStore.sortMenus.sortOrder).get();
  const sortDuration = useHookstate(libraryFilterStore.sortMenus.sortDuration).get();
  const { onVisibleIndexChange, restoreToIndex } = useVirtualScrollRestoration({
    keySegment: `${readingStatus}:${sortOrder}`,
  });

  useTitleOnNavigation(t("screens.library.manageLibrary"));

  const [mode, setMode] = useState<LayoutStyle["mode"]>("grid");
  useEffect(() => {
    if (!isSmall && mode === "list") {
      setMode("grid");
    }
  }, [isSmall]);

  const orders = useHookstate(appStore.init.comic_library_sort_orders).get({
    noproxy: true,
  });

  const {
    urlSettled,
    countData,
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useLibraryAPI();

  const queryClient = useQueryClient();
  const allPages = useMemo(() => {
    data?.pages.forEach((page) => {
      page.data.payload.results.forEach((n) => {
        const cacheKey = `${n.entity_type}-${n.id}`;
        queryClient.setQueryData([cacheKey], n);
      });
    });

    const allPages = (data?.pages ?? []) as AxiosResponse<
      ServerListResponse<Comic>
    >[];
    return allPages;
  }, [data]);

  const readingStatusCounts = useMemo(() => {
    const readingStatusCounts: { [key: string]: number } = {};
    countData?.data.payload.results.forEach((status) => {
      readingStatusCounts[
        status.comic_user_reading_status_id ?? DEFAULT_READING_STATUS_TYPE
      ] = status.count;
    });
    return readingStatusCounts;
  }, [countData]);

  const invalidate = useInvalidateQueries(GET_LIBRARY_LIST_QUERY_KEY);
  const searchQuery = useHookstate(libraryFilterStore.query).get();
  useEffect(() => {
    if (!urlSettled) return;
    const timeout = setTimeout(() => {
      invalidate();
    }, 100);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const [initTerm, setInitTerm] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (urlSettled) {
      setInitTerm(libraryFilterStore.query.get());
    }
  }, [urlSettled]);

  const throttleSearch = useCallback(
    throttle(async (term) => {
      if (!urlSettled) return;
      libraryFilterStore.query.set(term);
    }, 300),
    [urlSettled],
  );

  // Register search config with parent shelf tabs
  const { setSearchConfig } = useOutletContext<ShelfOutletContext>();
  useEffect(() => {
    setSearchConfig({
      placeholder: t("screens.library.searchPlaceholder"),
      onSearch: throttleSearch,
      initValue: initTerm,
    });
  }, [throttleSearch, initTerm]);


  const layout = libraryResultsCardV2(mode);
  const dims = sizeEstimator(layout.style.context, layout.style.mode, layout.style.size);

  return (
    <Flex
      direction="column"
      width="full"
      sx={{ WebkitOverflowScrolling: "touch" }}
    >
      <FluidVirtualGrid
        gridKey={`library-${mode}`}
        header={
          <Box
            paddingRight={isSmall ? "0px" : "40px"}
            position="sticky"
            top={subHeaderTop}
            transform={stickyTransform}
            transition={stickyTransition}
            zIndex={1}
            bg="charcoal.charcoal"
            mb="16px"
          >
            {isSmall && (
              <Box pl="16px" pt={5}>
                <ReadingStatusCarousel
                  readingStatusCounts={readingStatusCounts}
                  invalidate={invalidate}
                />
              </Box>
            )}
            <Flex
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
              pt={isMobile ? 2 : 5}
              pb="16px"
              px={isSmall ? "16px" : "0px"}
            >
              {!isSmall && (
                <ReadingStatusCarousel
                  readingStatusCounts={readingStatusCounts}
                  invalidate={invalidate}
                />
              )}
              <SortMenus
                invalidate={invalidate}
                sortOrder={sortOrder}
                sortDuration={sortDuration}
                onSortOrderChange={(value) => libraryFilterStore.sortMenus.sortOrder.set(value)}
                onSortDurationChange={(value) => libraryFilterStore.sortMenus.sortDuration.set(value)}
                sortOrders={orders}
              />
            </Flex>
          </Box>
        }
        gridHeightAdjustment={`${topBarHeight.get()}px`}
        layout={layout}
        minCardWidth={mode === "list" ? 9999 : dims.width}
        minColumns={mode === "list" ? 1 : 2}
        gap={mode === "list" ? 8 : 24}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        allPages={allPages}
        onVisibleIndexChange={onVisibleIndexChange}
        restoreToIndex={restoreToIndex}
      />
    </Flex>
  );
}

function libraryResultsCardV2(mode: LayoutStyle["mode"]) {
  const layout = comicsResultsCardV2(mode, false);
  return {
    display: {
      ...layout.display,
      include_ribbon_badge: false,
      include_gold_badge: false,
      include_reading_status_button: true,
    },
    style: layout.style,
  };
}
